import React from 'react';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { useTranslation } from 'react-i18next';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import {
  useClaim,
  useClaimPendingInfo,
} from 'structure/staking/hooks/useStakingContract';
import { waitForTransaction } from 'utils/waitForTransaction';
import { formatUnits } from 'utils/units';
import { InputTokenAmount } from 'structure/staking/components/form/InputTokenAmount';
import {
  useStakingPoolData,
  useStakingPoolList,
} from 'structure/staking/hooks/useStakingAPI';
import { ethers } from 'ethers';
import { useConnection } from 'wagmi';
import { useForm } from 'react-hook-form';
import { useParams } from 'next/navigation';
import { Title4 } from 'components/Typography';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import { StakingPool } from 'api/StakingAPI';

type DepositModalProps = {
  open: boolean;
  poolId: number;
  onOpenChange: (isOpen: boolean) => void;
};

export function ModalStakeClaim({
  open,
  poolId,
  onOpenChange,
}: DepositModalProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile(834);
  const toastV2 = useToast();
  const account = useConnection();
  const params = useParams<{ id: string }>();

  const { data = [] } = useStakingPoolList({}, params?.id ?? '');
  const initialPool = data.find((pool) => pool.id === poolId);
  const stakingData = useStakingPoolData(initialPool as StakingPool);
  const pool = stakingData?.data;
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const { data: claimableAmount } = useClaimPendingInfo({
    contractAddress: pool.stakingContractAddress,
    address: account.address as `0x${string}`,
    enabled: !!account.address && open,
  });

  const bnClaimable = ethers.BigNumber.from(claimableAmount || '0');

  const claim = useClaim({
    contractAddress: pool.stakingContractAddress,
    enabled: open && !bnClaimable.isZero(),
  });

  const claimable = parseFloat(
    formatUnits(bnClaimable, pool.rewardsTokenDecimals).toString()
  );

  const form = useForm();

  const handleClaim = async () => {
    if (claim.prepare.isError) {
      toastV2({
        title: claim.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_CLAIM'),
        type: 'error',
      });
      return;
    }

    try {
      const result = await claim.contract.writeContractAsync(
        claim.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toastV2({
        title: t('STAKING.CLAIM_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });
      handleOpenChange(false);
    } catch (error) {
      console.log('### claim error', error);
      const message = getTxErrorMessage(
        error as Error,
        t('common:ERROR_MESSAGES.UNKNOWN_ERROR')
      );

      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: message,
        type: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    await handleClaim();
  };

  return (
    <ModalV2.Root open={open} onOpenChange={handleOpenChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={isMobile}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className='flex flex-row items-center justify-between'>
            <ModalV2.Title variant='dark'>
              <Title4>{t('STAKING.MODAL_CLAIM_TITLE')}</Title4>
            </ModalV2.Title>
            <ModalV2.Close variant='dark' />
          </div>
          <ModalV2.Body variant='dark' className='rounded-t-main pb-6 pt-6'>
            <InputTokenAmount
              value={claimable}
              disabled
              maxcontrol={false}
              onChange={() => {}}
              tokenSymbol={pool.depositTokenSymbol}
            />
          </ModalV2.Body>
          <div className='mt-6 flex justify-end gap-4'>
            <Button
              onClick={() => handleOpenChange(false)}
              disabled={claim.contract.isPending}
              style='outline'
            >
              {t('STAKING.CANCEL')}
            </Button>

            <Button
              type='submit'
              loading={
                claim.contract.isPending ||
                claim.prepare.isFetching ||
                form.formState.isSubmitting
              }
              disabled={
                claim.contract.isPending ||
                claim.prepare.isFetching ||
                form.formState.isSubmitting ||
                claimable <= 0
              }
            >
              {t('STAKING.CLAIM')}
            </Button>
          </div>
        </form>
      </ModalV2.Content>
    </ModalV2.Root>
  );
}
